import React, { useContext, useMemo, useState, useEffect } from "react";
import Table from '@mui/material/Table';
import Box from '@mui/material/Box';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import TablePagination from '@mui/material/TablePagination';
import ReportContext from "../../../context/ReportsContext/ReportContext";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
  DroppableProvided,
  DraggableProvided
} from "react-beautiful-dnd";
// @ts-ignore
import "../css/TableDynamic.css";

type ReportRow = Record<string, any>;
interface GroupedRow {
  groupValue: string | null;
  rows: ReportRow[];
}

const TableDynamic = () => {
  const { state } = useContext(ReportContext);

  const report: ReportRow[] = useMemo(
    () => state?.reportPreviewData?.report || [],
    [state?.reportPreviewData?.report]
  );

  const visibleColumns: string[] = useMemo(
    () => state?.reportPreviewData?.columnasVisibles || [],
    [state?.reportPreviewData?.columnasVisibles]
  );

  const agrupador: string | null = state?.reportPreviewData?.agrupadorSeleccionado || null;
  
  const [columnsOrder, setColumnsOrder] = useState<string[]>(visibleColumns);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState<number | -1>(10);

  useEffect(() => {
    setColumnsOrder((prev) =>
      visibleColumns
        .filter((col) => prev.includes(col))
        .concat(visibleColumns.filter((col) => !prev.includes(col)))
    );
  }, [visibleColumns]);

  const groupedRows: GroupedRow[] = useMemo(() => {
    if (!agrupador) return [{ groupValue: null, rows: report }];

    const groups: Record<string, ReportRow[]> = {};
    report.forEach((row) => {
      const key = row[agrupador] ?? "Sin valor";
      if (!groups[key]) groups[key] = [];
      groups[key].push(row);
    });

    return Object.entries(groups).map(([groupValue, rows]) => ({ groupValue, rows }));
  }, [report, agrupador]);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const newOrder = Array.from(columnsOrder);
    const [removed] = newOrder.splice(result.source.index, 1);
    newOrder.splice(result.destination.index, 0, removed);

    setColumnsOrder(newOrder);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);
    setRowsPerPage(value);
    setPage(0);
  };

  if (!report.length) return <div style={{ padding: 16 }}>Sin datos para mostrar </div>;
  if (!visibleColumns.length) return <div style={{ padding: 16 }}>No hay columnas visibles</div>;
  const rowsWithGroupInfo: { row: ReportRow; groupValue: string | null }[] = [];
  groupedRows.forEach(group => {
    group.rows.forEach(row => {
      rowsWithGroupInfo.push({ row, groupValue: group.groupValue });
    });
  });

  const paginatedRows = rowsPerPage === -1 
    ? rowsWithGroupInfo 
    : rowsWithGroupInfo.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box className="containerTableDynamic">
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }}>
          <DragDropContext onDragEnd={handleDragEnd}>
            <TableHead>
              <Droppable droppableId="columns" direction="horizontal">
                {(provided: DroppableProvided) => (
                  <TableRow ref={provided.innerRef} {...provided.droppableProps} className="pd_contenedorTitulos">
                    {columnsOrder
                      .filter((col) => visibleColumns.includes(col))
                      .map((col, index) => (
                        <Draggable key={col} draggableId={col} index={index}>
                          {(provided: DraggableProvided) => (
                            <TableCell
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              style={{ ...provided.draggableProps.style }}
                            >
                              {col}
                            </TableCell>
                          )}
                        </Draggable>
                      ))}
                    {provided.placeholder}
                  </TableRow>
                )}
              </Droppable>
            </TableHead>

            <TableBody>
              {paginatedRows.map(({ row, groupValue }, i) => (
                <React.Fragment key={`row-${i}`}>
                  {groupValue !== null && (i === 0 || groupValue !== paginatedRows[i - 1].groupValue) && (
                    <TableRow style={{ background: "#f0f0f0" }}>
                      <TableCell colSpan={columnsOrder.length}>
                        {agrupador}: {groupValue}
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow>
                    {columnsOrder
                      .filter((col) => visibleColumns.includes(col))
                      .map((col) => (
                        <TableCell key={col}>{row[col]}</TableCell>
                      ))}
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </DragDropContext>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50, { label: 'Todos', value: -1 }]}
          component="div"
          count={rowsWithGroupInfo.length}
          rowsPerPage={rowsPerPage === -1 ? rowsWithGroupInfo.length : rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          showFirstButton
          showLastButton
        />
      </TableContainer>
    </Box>
  );
};

export default TableDynamic;
